import { Container, Spinner, Row, Button, Form, Col } from 'react-bootstrap';
import styled from 'styled-components';
import React, { useEffect, useState, useRef } from 'react';
import { ToReadableDate, DecryptOld } from '../../../utils';
import {
    UploadedPolicyGetAllService,
    ClaimTypeGetService,
    ClaimDocTypeGetService,
    DocPerClaimTypeGetService,
    ClaimSaveService
} from '../../../domain';
import {
    ClaimReviewShowPolicyView,
    ClaimReviewShowClaimsView,
    ClaimReviewShowDetailsView,
    ClaimReviewShowDocumentsView
} from './index';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end; /* Align items to the right */
    margin-bottom: 20px;
    position: relative;
`;

const HeaderText = styled.span`
    font-family: 'Montserrat';
    font-weight: 700;
    font-size: 20px;
    color: rgba(41, 37, 97, 1);
    background: rgb(234, 222, 208, 1); /* Match the background color */
    padding-right: 10px;
    z-index: 1;
    position: relative;
`;

const StyledHR = styled.hr`
    flex-grow: 1;
    border: 0;
    height: 2px;
    background-color: rgba(41, 37, 97, 1);
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    z-index: 0;
`;

const SuperTitle = styled.h1`
    font-size: 40px;
    font-weight: 700;
`;

const Title = styled.h1`
    font-size: 34px;
    font-weight: 700;
`;

const StyledContainer = styled(Container)`
    font-family: 'Montserrat';
    color: rgba(41, 37, 97, 1);
    background: rgb(234, 222, 208, 1);
    padding: 20px 20%;
    min-height: 100vh;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 100%;
        padding: 2% 10%;
    }
`;

const Loading = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.5);
    z-index: 2000;
`;

const StyledSubmitButton = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 40px;
    border-radius: 25px;
    font-family: 'Montserrat';
    font-weight: 700;
    font-size: 18px;
    color: white;
    text-align: center;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 14px;
        padding: 5px 20px;
        margin-top: 12px;
        margin-bottom: 12px;
    }
`;

export default function Claim() {
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [formData, setFormData] = useState(null);
    const [claimTypes, setClaimTypes] = useState(null);
    const [docPerClaimTypes, setDocPerClaimTypes] = useState(null);
    const [userName, setUserName] = useState('');
    const navigate = useNavigate();

    const _UploadedPolicyGetAllService = new UploadedPolicyGetAllService();
    const _ClaimTypeGetService = new ClaimTypeGetService();
    const _ClaimDocTypeGetService = new ClaimDocTypeGetService();
    const _DocPerClaimTypeGetService = new DocPerClaimTypeGetService();
    const _ClaimSaveService = new ClaimSaveService();

    const debounceTimeout = useRef(null);

    const getPoliciesData = async (page) => {
        setLoading(true);

        const response = await _UploadedPolicyGetAllService.execute({
            search: search || '',
            page: page || 1,
            size: 5
        });

        if (!Object.keys(response.data).length) {
            Swal.fire({
                text: 'Something went wrong. Please try again later.',
                icon: 'error',
                iconColor: 'black',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            setLoading(false);
            setFormData(null);
            return;
        }

        setFormData({
            availablePolicy: response.data
        });

        setLoading(false);
    };

    const mapDocumentPerClaimType = async () => {
        try {
            setLoading(true);
            const [docPerClaimTypes, claimTypes, documentTypes] = await Promise.all([
                _DocPerClaimTypeGetService.execute(),
                _ClaimTypeGetService.execute(),
                _ClaimDocTypeGetService.execute()
            ]);

            if (!docPerClaimTypes.documentPerClaimTypes || !claimTypes.claimTypes || !documentTypes.documentTypes) {
                Swal.fire({
                    text: 'Something went wrong. Please try again later.',
                    icon: 'error',
                    iconColor: 'black',
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Ok',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                });
                setLoading(false);
                return;
            }
            setClaimTypes(claimTypes.claimTypes);

            // map the data
            const result = docPerClaimTypes.documentPerClaimTypes.map((docPerClaim) => {
                const claimType = claimTypes.claimTypes.find((claim) => claim.id === docPerClaim.mtnClaimTypeId);
                const documentType = documentTypes.documentTypes.find(
                    (doc) => doc.id === docPerClaim.mtnDocumentTypeId
                );

                return {
                    id: docPerClaim.id,
                    mtnClaimTypeId: docPerClaim.mtnClaimTypeId,
                    mtnDocumentTypeId: docPerClaim.mtnDocumentTypeId,
                    claimTypeDesc: claimType ? claimType.claimTypeDesc : null,
                    claimTypeCd: claimType ? claimType.claimTypeCd : null,
                    documentDesc: documentType ? documentType.documentDesc : null
                };
            });

            setDocPerClaimTypes(result);
            setLoading(false);

            return result;
        } catch (error) {
            Swal.fire({
                text: 'Something went wrong. Please try again later.',
                icon: 'error',
                iconColor: 'black',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            console.error('Error:', error);
            setLoading(false);
        }
    };

    const handleFormSubmit = async () => {
        try {
            if (!formData.selectedClaim || !formData.selectedPolicy) {
                Swal.fire({
                    text: 'Insufficient Information to Update.',
                    icon: 'error',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                });
                return;
            }
            
            const setPayload = {
                ...formData.claimsPayload,
                claimItems: formData.claimsPayload.claimItems.map(claimitem => ({
                    ...claimitem,
                    lastUpdateBy: userName ? userName : 'hcc-admin',
                })),
                claimDocuments: [
                    ...(formData.claimsPayload.claimDocuments.map(docitem => ({
                        ...docitem,
                        attachedDocuments: [
                            ...(docitem.attachedDocuments.map(docitemattached => ({
                                ...docitemattached,
                                lastUpdateBy: userName ? userName : 'hcc-admin',
                            }))),
                        ]
                    }))),
                ],
                customerDetails: {
                    ...formData.claimsPayload.customerDetails,
                },
                lastUpdateBy: userName ? userName : 'hcc-admin',
            };
            console.log(setPayload);

            Swal.fire({
                title: 'Confirm',
                text: 'Submit Claim?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Proceed',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    setLoading(true);
                    // const response = await _ClaimSaveService.execute(formData.claimsPayload);
                    const response = await _ClaimSaveService.execute(setPayload);

                    if (!response.claim) {
                        Swal.fire({
                            text: 'Sorry. Something went wrong. Please try again later.',
                            icon: 'warning',
                            confirmButtonColor: '#3085d6',
                            confirmButtonText: 'OK',
                            customClass: {
                                popup: 'my-popup',
                                confirmButton: 'my-confirm-button',
                                cancelButton: 'my-cancel-button'
                            }
                        });
                        return;
                    }

                    setSearch('');

                    Swal.fire({
                        text: 'Claims Review has been Completed',
                        icon: 'success',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: 'OK',
                        customClass: {
                            popup: 'my-popup',
                            confirmButton: 'my-confirm-button',
                            cancelButton: 'my-cancel-button'
                        }
                    });
                    setLoading(false);
                }
            });
        } catch (error) {
            Swal.fire({
                text: 'Something went wrong. Please try again later.',
                icon: 'error',
                iconColor: 'black',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            console.error('Error:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        let idleTimeout;
        let sessionTimeout;
    
        const resetIdleTimer = () => {
            clearTimeout(idleTimeout);
            idleTimeout = setTimeout(() => {
                startSessionExpirationTimer();
            }, 15 * 60 * 1000); // 5 minutes
        };
    
        const startSessionExpirationTimer = () => {
            clearTimeout(sessionTimeout);
            sessionTimeout = setTimeout(() => {
                Swal.fire({
                    text: 'You\'ve been idle for 5 minutes. Please generate a new session.',
                    icon: 'error',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                }).then(() => {
                    navigate('/home');
                });
            }, 15 * 60 * 1000); // 5 minutes
        };
    
        const validateSession = async () => {
            const hash = window.location.hash;
            const queryParams = new URLSearchParams(hash.split('?')[1]);
            const session = queryParams.get('session');
            const decodedSession = decodeURIComponent(session).replace(/ /g, '+');
    
            if (!decodedSession) {
                Swal.fire({
                    text: 'Invalid session.',
                    icon: 'error',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                }).then(() => {
                    navigate('/home');
                });
                return;
            }
    
            const decryptedSession = DecryptOld(decodedSession);
            console.log('Raw Decrypted Session:', decryptedSession);
    
            let cleanedSession = decryptedSession.trim();
            cleanedSession = cleanedSession.replace(/\\/g, '').replace(/\\n/g, '');
            console.log('Cleaned Decrypted Session:', cleanedSession);
    
            if (cleanedSession.startsWith('"') && cleanedSession.endsWith('"')) {
                cleanedSession = cleanedSession.slice(1, -1);
            }
    
            let sessionData;
            try {
                sessionData = JSON.parse(cleanedSession);
                console.log('Parsed Session Data:', sessionData);
                console.log('Parsed Session Time:', sessionData.time);
                console.log('Parsed Session Name:', sessionData.name);
            } catch (error) {
                console.error('Failed to parse decrypted session:', error);
                Swal.fire({
                    text: 'Invalid session format. Please try again.',
                    icon: 'error',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                }).then(() => {
                    navigate('/home');
                });
                return;
            }
            
            const sessionTime = new Date(sessionData.time);
            setUserName(sessionData.name)
            console.log('sessionTime', sessionTime);
            const currentTime = new Date();
    
            // Check if the session time plus 5 minutes has passed
            const sessionExpirationTime = new Date(sessionTime.getTime() + 60 * 60 * 1000);
            if (currentTime > sessionExpirationTime) {
                Swal.fire({
                    text: 'Your session has expired due to inactivity. Please log in again to the Express Protect Admin portal to continue using the claims review module.',
                    icon: 'error',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                }).then(() => {
                    navigate('/home');
                });
                return;
            }
        };
    
        const resetTimersOnActivity = () => {
            resetIdleTimer();
        };
    
        // Add event listeners for user activity
        window.addEventListener('mousemove', resetTimersOnActivity);
        window.addEventListener('keydown', resetTimersOnActivity);
        window.addEventListener('click', resetTimersOnActivity);
        window.addEventListener('scroll', resetTimersOnActivity);
    
        validateSession();
        resetIdleTimer();
        return () => {
            // Clean up event listeners and timers
            window.removeEventListener('mousemove', resetTimersOnActivity);
            window.removeEventListener('keydown', resetTimersOnActivity);
            window.removeEventListener('click', resetTimersOnActivity);
            window.removeEventListener('scroll', resetTimersOnActivity);
            clearTimeout(idleTimeout);
            clearTimeout(sessionTimeout);
        };
    }, [navigate]);

    useEffect(() => {
        mapDocumentPerClaimType();
        getPoliciesData();
    }, []);

    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (formData) {
                getPoliciesData();
            }
        }, 1000);

        return () => {
            clearTimeout(debounceTimeout.current);
        };
    }, [search]);

    return (
        <>
            {loading && (
                <Loading>
                    <Spinner animation="border" variant="light" />
                </Loading>
            )}
            <StyledContainer id="contact-us" fluid>
                <Header>
                    <HeaderText>{userName ? userName : 'HELLO'}</HeaderText>
                    <StyledHR />
                </Header>
                <Row>
                    <SuperTitle>Claims Review</SuperTitle>
                </Row>

                <br />

                <Row>
                    <Title>Policy</Title>
                </Row>

                <br />

                <Row>
                    <Col lg={5}>
                        <Form.Control
                            type="text"
                            name="search"
                            placeholder="Search Loan Account Number"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Col>

                    {/* <StyledSubmitButton
                        variant="secondary"
                        type="button"
                        onClick={(e) => {
                            getPoliciesData();
                        }}
                    >
                        Search
                    </StyledSubmitButton> */}
                </Row>

                <ClaimReviewShowPolicyView
                    formData={formData}
                    setFormData={setFormData}
                    setLoading={setLoading}
                    getPoliciesData={getPoliciesData}
                />

                {formData?.availableClaims && (
                    <ClaimReviewShowClaimsView
                        formData={formData}
                        setFormData={setFormData}
                        setLoading={setLoading}
                        claimTypes={claimTypes}
                        docPerClaimTypes={docPerClaimTypes}
                    />
                )}

                {!!formData?.availableClaims?.claims?.length && formData?.selectedClaim && (
                    <ClaimReviewShowDetailsView formData={formData} setFormData={setFormData} />
                )}

                {!!formData?.availableClaims?.claims?.length && formData?.selectedClaim && formData?.claimsPayload?.claimDocuments && (
                    <ClaimReviewShowDocumentsView
                        formData={formData}
                        setFormData={setFormData}
                        docPerClaimTypes={docPerClaimTypes}
                        setLoading={setLoading}
                    />
                )}

                <br />

                <Row>
                    <div className="gap-2 justify-content-left mt-2">
                        <StyledSubmitButton
                            variant="secondary me-2"
                            type="submit"
                            onClick={(e) => {
                                if (search) {
                                    setSearch('');
                                } else {
                                    getPoliciesData();
                                }
                            }}
                        >
                            CLEAR
                        </StyledSubmitButton>

                        <StyledSubmitButton
                            className=""
                            variant="secondary"
                            type="submit"
                            onClick={() => handleFormSubmit()}
                        >
                            SUBMIT
                        </StyledSubmitButton>
                    </div>
                </Row>
            </StyledContainer>
        </>
    );
}
